import React, { useCallback, useMemo, useState } from "react"

import { OneVerifyPermission } from "@common-shared/config/rolesPermissions.config"

import { ButtonElement } from "@front-shared/elements/button.element"
import { InputElement } from "@front-shared/elements/input.element"

import { RuntimeHelper } from "../../../helpers/runtime.helper"
import { UtilHelper } from "../../../helpers/util.helper"
import { useHasPermissions } from "../../../hooks/hasPermissions.hook"
import { useAppDispatch } from "../../../store/appDispatch.hook"
import { useAppSelector } from "../../../store/selectors/app.selector"
import { payerMappingsSelector } from "../../../store/selectors/payerMappings.selector"
import { runtimeSelector } from "../../../store/selectors/runtime.selector"
import { selectedPracticeRoleSelector } from "../../../store/selectors/selectedPracticeRole.selector"
import { payerMappingsGetList } from "../../../store/thunks/payerMappings.thunks"
import type { IReviewPayerMappingsState } from "../../../types/payerMappings.types"
import { PayerMappingsBulkUploadPopupComponent } from "../../popups/payerMappingsBulkUploadPopup.component"
import { UpsertPayerMappingPopupComponent } from "../../popups/upsertPayerMappingPopup.component"
import { PayerMappingsCsvDropdownComponent } from "./payerMappingsCsvDropdown.component"

import plusIcon from "@front-shared/assets/images/icons/plus-dark.svg"
import searchIcon from "@front-shared/assets/images/icons/search-dark.svg"

export const PayerMappingsManageComponent: React.FunctionComponent<
    IPayerMappingsManageComponentProps
> = props => {
    const dispatch = useAppDispatch()

    const runtimeState = useAppSelector(runtimeSelector)

    const payerMappings = useAppSelector(payerMappingsSelector)

    const selectedPracticeRole = useAppSelector(selectedPracticeRoleSelector)

    const [isAddingMapping, setIsAddingMapping] = useState<boolean>(false)
    const [isBulkUploadPopupActive, setIsBulkUploadPopupActive] =
        useState<boolean>(false)

    // The search input is uncontrolled, so remounting is the only way to clear it
    const [searchInputKey, setSearchInputKey] = useState<number>(0)

    const isCreatingPayerMapping = useMemo<boolean>(() => {
        return RuntimeHelper.isPayerMappingsCreateLoading()
    }, [runtimeState.isLoading])

    const canManagePracticeConfigurations = useHasPermissions(
        OneVerifyPermission.managePracticeConfigurations,
        undefined,
        false,
    )

    // The list is newest first, so a new mapping is the top row of the first page.
    // The search is dropped rather than reapplied: payer-service matches the payer id
    // exactly, so a mapping added under any other id would be filtered straight back
    // out of the list that is meant to be showing it.
    const onAddingMappingDone = () => {
        setSearchInputKey(current => current + 1)

        // Reset the list one of two ways depending on whether a search is active
        if (props.externalPayerIdSearch) {
            // the table reloads page 1 off this change on its own
            props.onExternalPayerIdSearchChange(undefined)
        } else if (selectedPracticeRole) {
            dispatch(
                payerMappingsGetList({
                    practice: selectedPracticeRole.practice,
                    payload: UtilHelper.getPagination(
                        payerMappings.itemsPagination.count,
                    ),
                    ignorePreloader: true,
                }),
            )
        }

        setIsAddingMapping(false)
    }

    const onAddMapping = () => {
        if (!canManagePracticeConfigurations) {
            return
        }

        setIsAddingMapping(true)
    }

    const onSearchChanged = useCallback(
        (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const value = event.target.value.trim()

            props.onExternalPayerIdSearchChange(value || undefined)
        },
        [props.onExternalPayerIdSearchChange],
    )

    return (
        <div className="max-w-[840px] mx-auto relative py-[24px]">
            <div className="flex items-center gap-[24px] justify-between sm:block">
                <div className="flex items-center gap-[20px]">
                    {canManagePracticeConfigurations && (
                        <ButtonElement
                            dataProduktly="payer-mappings-add"
                            label="Add Payer Mapping"
                            icon={plusIcon}
                            onClick={onAddMapping}
                            isLoading={isCreatingPayerMapping}
                        />
                    )}

                    <div data-produktly="payer-mappings-csv">
                        <PayerMappingsCsvDropdownComponent
                            onOpenBulkUpload={() => {
                                setIsBulkUploadPopupActive(true)
                            }}
                        />
                    </div>
                </div>

                <div data-produktly="payer-mappings-search">
                    <InputElement
                        key={searchInputKey}
                        name="externalPayerIdSearch"
                        className="w-[250px] sm:w-full sm:mt-[24px]"
                        placeholder="Search by Payer ID"
                        onChange={onSearchChanged}
                        debounce={500}
                        allowClear={true}
                        inputSuffix={
                            <img
                                src={searchIcon}
                                alt="Search"
                                title="Search"
                                className="ml-[5px]"
                            />
                        }
                    />
                </div>
            </div>

            {isAddingMapping && (
                <UpsertPayerMappingPopupComponent
                    isActive={isAddingMapping}
                    onDone={onAddingMappingDone}
                    onClose={() => {
                        setIsAddingMapping(false)
                    }}
                />
            )}

            {isBulkUploadPopupActive && (
                <PayerMappingsBulkUploadPopupComponent
                    isActive={isBulkUploadPopupActive}
                    onDone={() => {
                        setIsBulkUploadPopupActive(false)
                    }}
                    onValidationDone={data => {
                        setIsBulkUploadPopupActive(false)
                        props.onValidationDone(data)
                    }}
                />
            )}
        </div>
    )
}

interface IPayerMappingsManageComponentProps {
    externalPayerIdSearch?: string
    onExternalPayerIdSearchChange: (value?: string) => void
    onValidationDone: (data: IReviewPayerMappingsState) => void
}
